import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../../core/models/base-state/base-state';
import { getHealthSummary, setGlobalPageSize } from '../../../../../core/store/actions/app.actions';
import { PatientProblem } from '../../../models/patient-problem';
import * as problemActions from '../actions/patient-problem.actions';

export interface PatientProblemsState extends EntityState<PatientProblem> {
  baseState: baseState.BaseState;
  pageSize: number;
  isFullDatasetLoaded: boolean;
  cachePatientId: number | null;
  cacheLoadedAt: number | null;
  isFullDatasetLoading: boolean;
  fullDatasetError: unknown;
}

export const patientProblemsAdapter = createEntityAdapter<PatientProblem>({
  selectId: patientProblem => patientProblem.patientProblemId,
});

export const initialState: PatientProblemsState = patientProblemsAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  pageSize: 5,
  isFullDatasetLoaded: false,
  cachePatientId: null,
  cacheLoadedAt: null,
  isFullDatasetLoading: false,
  fullDatasetError: null,
});

export const patientProblemsFeatureKey = 'patient-problems';

export const patientProblemsReducer = createReducer(
  initialState,
  on(problemActions.getPatientProblems, getHealthSummary, (state): PatientProblemsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
      fullDatasetError: null,
    };
  }),

  on(
    problemActions.getPatientProblemsSuccess,
    (state, { patientProblems, pageNumber, isAtEndOfData, totalRecordCount }): PatientProblemsState => {
      const nextState =
        pageNumber <= 1
          ? patientProblemsAdapter.setAll(patientProblems, state)
          : patientProblemsAdapter.addMany(patientProblems, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(problemActions.getPatientProblemsFailure, (state, { error }): PatientProblemsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      fullDatasetError: null,
    };
  }),

  on(
    problemActions.getAllPatientProblems,
    (state): PatientProblemsState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
      },
      isFullDatasetLoading: true,
      fullDatasetError: null,
    })
  ),

  on(
    problemActions.getAllPatientProblemsSuccess,
    (state, { patientProblems, patientId, loadedAt }): PatientProblemsState => {
      const nextState = patientProblemsAdapter.setAll(patientProblems, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          error: null,
          isLoading: false,
          pageNumber: 1,
          isAtEndOfData: true,
          totalRecordCount: patientProblems.length,
        },
        isFullDatasetLoaded: true,
        cachePatientId: patientId,
        cacheLoadedAt: loadedAt,
        isFullDatasetLoading: false,
        fullDatasetError: null,
      };
    }
  ),

  on(
    problemActions.getAllPatientProblemsFailure,
    (state, { error }): PatientProblemsState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
      isFullDatasetLoading: false,
      fullDatasetError: error,
    })
  ),

  on(
    problemActions.invalidateProblemCache,
    (state): PatientProblemsState => ({
      ...state,
      isFullDatasetLoaded: false,
      cachePatientId: null,
      cacheLoadedAt: null,
      isFullDatasetLoading: false,
      fullDatasetError: null,
    })
  ),

  on(problemActions.setPageNumber, (state, { pageNumber }): PatientProblemsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber,
      },
    };
  }),

  on(
    setGlobalPageSize,
    (state, { pageSize }): PatientProblemsState => ({
      ...state,
      pageSize,
    })
  )
);
